/**
 * Ranges, formatting and the drill-down link for the AI Service Analytics tab
 * (task 1255). Everything here is a pure function of its inputs so the tab and
 * its tests agree on what "Last 30 days" and a day's link mean.
 */
import type { AnalyticsDay } from '@/types/analytics';

/**
 * The picker's presets, in the design file's order (task 1304). `days` is the
 * length of a trailing preset ending today; `today`, month to date and the
 * custom range work their dates out in `rangeFor`.
 */
export type AnalyticsRangeKey = 'today' | '7d' | '30d' | '90d' | 'mtd' | 'custom';
export const ANALYTICS_RANGES: readonly { key: AnalyticsRangeKey; label: string; days?: number }[] = [
  { key: 'today', label: 'Today', days: 1 },
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'mtd', label: 'Month to date' },
  { key: 'custom', label: 'Custom range…' },
];
export const DEFAULT_ANALYTICS_RANGE: AnalyticsRangeKey = '30d';
export const isAnalyticsRangeKey = (value: string | null): value is AnalyticsRangeKey =>
  ANALYTICS_RANGES.some((r) => r.key === value);

/** The route's cap on a range, inclusive of both ends (task 1254). */
export const ANALYTICS_MAX_DAYS = 90;

const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` for a UTC instant. Days are UTC on both sides of the route. */
export const isoDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const dayMs = (date: string): number => Date.parse(`${date}T00:00:00Z`);

/** How many days an inclusive `from`..`to` range covers: Oct 1 to Oct 1 is one. */
export const spanDays = (from: string, to: string): number => Math.round((dayMs(to) - dayMs(from)) / DAY_MS) + 1;

/**
 * The inclusive `from`/`to` ISO dates for a preset, in UTC: "Last 30 days" is
 * today and the 29 days before it, "Today" is today alone and "Month to date"
 * runs from the first of this month. `custom` is the pair the User chose; a
 * custom key with no valid pair falls back to the default preset.
 */
export function rangeFor(
  key: AnalyticsRangeKey,
  now: number = Date.now(),
  custom?: { from: string; to: string }
): { from: string; to: string } {
  const to = isoDay(now);
  if (key === 'custom' && custom && validateCustomRange(custom.from, custom.to, now) === null) return { ...custom };
  if (key === 'mtd') return { from: `${to.slice(0, 7)}-01`, to };
  const preset = ANALYTICS_RANGES.find((r) => r.key === key && r.days) ?? ANALYTICS_RANGES.find((r) => r.key === DEFAULT_ANALYTICS_RANGE)!;
  return { from: isoDay(now - ((preset.days ?? 30) - 1) * DAY_MS), to };
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Why a custom range cannot be sent, as the one line the picker shows, or null
 * when it can: both dates set, from on or before to, to no later than today
 * (UTC), and no longer than the route's 90-day cap.
 */
export function validateCustomRange(from: string, to: string, now: number = Date.now()): string | null {
  if (!ISO_DAY.test(from) || !ISO_DAY.test(to) || Number.isNaN(dayMs(from)) || Number.isNaN(dayMs(to))) {
    return 'Enter both dates.';
  }
  if (from > to) return 'The start date must be on or before the end date.';
  if (to > isoDay(now)) return 'The end date cannot be after today.';
  if (spanDays(from, to) > ANALYTICS_MAX_DAYS) return `Choose a range of ${ANALYTICS_MAX_DAYS} days or fewer.`;
  return null;
}

/**
 * The period a range is compared with: the same number of days, ending the
 * day before `from`. Both windows sit inside the route's cap on their own.
 */
export function comparisonRangeFor(from: string, to: string): { from: string; to: string } {
  const days = spanDays(from, to);
  const end = dayMs(from) - DAY_MS;
  return { from: isoDay(end - (days - 1) * DAY_MS), to: isoDay(end) };
}

/** "Oct 14" for an ISO day, read as UTC so the label never shifts a day. */
export function formatDay(date: string): string {
  return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

const integer = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
export const formatCount = (n: number): string => integer.format(n);

/** Milliseconds as "850 ms", "2.9 s" or "1.4 min". */
export function formatDurationMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

/** Cents below a dollar keep four places, so a $0.0137 run does not read as $0.01. */
export function formatUsd(n: number): string {
  const digits = n !== 0 && Math.abs(n) < 1 ? 4 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: digits,
  }).format(n);
}

/** A 0..1 share as "1.4%". */
export function formatPercent(share: number): string {
  const pct = share * 100;
  return `${pct >= 10 || pct === 0 ? Math.round(pct) : pct.toFixed(1)}%`;
}

/** The headline counts: production runs only; tests are said apart. */
export function totalsOf(days: AnalyticsDay[]) {
  return days.reduce(
    (acc, d) => ({
      runs: acc.runs + d.runs,
      testRuns: acc.testRuns + d.testRuns,
      tokens: acc.tokens + d.tokens,
      costUsd: acc.costUsd + (d.costUsd ?? 0),
    }),
    { runs: 0, testRuns: 0, tokens: 0, costUsd: 0 }
  );
}

/**
 * A run-weighted median of one daily figure: the value below which half the
 * period's runs fall when every day's runs are taken to sit at that day's
 * figure. Days with no figure (no successful run) or no runs are left out.
 */
function runWeightedMedian(days: AnalyticsDay[], pick: (d: AnalyticsDay) => number | null): number | null {
  const points = days
    .map((d) => ({ value: pick(d), weight: d.runs }))
    .filter((p): p is { value: number; weight: number } => p.value !== null && p.weight > 0)
    .sort((a, b) => a.value - b.value);
  const total = points.reduce((sum, p) => sum + p.weight, 0);
  if (total === 0) return null;
  let seen = 0;
  for (const p of points) {
    seen += p.weight;
    if (seen >= total / 2) return p.value;
  }
  return points[points.length - 1].value;
}

/**
 * The four stat tiles' figures for one window (task 1304), production runs
 * only; tests never reach a tile.
 *
 * The route serves each day's median and p90, not the runs behind them, so
 * the period's median duration is the median of the daily medians weighted by
 * each day's runs: a busy day counts for as many runs as it had, which is a
 * fair "median over the period" without per-run data. The slowest tenth is the
 * same weighting over the daily p90s, the typical day's slowest tenth.
 */
export function periodStatsOf(days: AnalyticsDay[]) {
  const totals = totalsOf(days);
  const failedRuns = Math.round(days.reduce((sum, d) => sum + (d.errorRate ?? 0) * d.runs, 0));
  return {
    runs: totals.runs,
    runsPerDay: days.length > 0 ? totals.runs / days.length : 0,
    medianMs: runWeightedMedian(days, (d) => d.duration.median),
    p90Ms: runWeightedMedian(days, (d) => d.duration.p90),
    costUsd: totals.costUsd,
    costPerRun: totals.runs > 0 ? totals.costUsd / totals.runs : null,
    failedRuns,
    errorRate: totals.runs > 0 ? failedRuns / totals.runs : null,
  };
}
export type PeriodStats = ReturnType<typeof periodStatsOf>;

/** Whether a change reads as better or worse for the metric, or neither. */
export type DeltaTone = 'good' | 'bad' | 'neutral';
export interface StatDelta {
  text: string;
  tone: DeltaTone;
}

const MINUS = '\u2212';

/**
 * The change against the comparison period as a signed percentage, toned by
 * whether more is good for this metric (runs) or bad (duration, cost). Null
 * when there is nothing to compare with.
 */
export function percentDelta(current: number | null, previous: number | null, moreIsGood: boolean): StatDelta | null {
  if (current === null || previous === null || previous === 0) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return { text: '0%', tone: 'neutral' };
  const up = pct > 0;
  return { text: `${up ? '+' : MINUS}${Math.abs(pct)}%`, tone: up === moreIsGood ? 'good' : 'bad' };
}

/** The error rate's change in percentage points: "−0.3 pt", fewer errors good. */
export function pointDelta(current: number | null, previous: number | null): StatDelta | null {
  if (current === null || previous === null) return null;
  const pt = Math.round((current - previous) * 1000) / 10;
  if (pt === 0) return { text: '0 pt', tone: 'neutral' };
  return { text: `${pt > 0 ? '+' : MINUS}${Math.abs(pt)} pt`, tone: pt > 0 ? 'bad' : 'good' };
}

/**
 * How the empty state names the period: "in the last 30 days", "today",
 * "this month so far" or "from Oct 1 to Oct 9".
 */
export function periodPhrase(key: AnalyticsRangeKey, from: string, to: string): string {
  if (key === 'today') return 'today';
  if (key === 'mtd') return 'this month so far';
  if (key === 'custom') return `from ${formatDay(from)} to ${formatDay(to)}`;
  return `in the ${(ANALYTICS_RANGES.find((r) => r.key === key)?.label ?? '').toLowerCase()}`;
}

/** True when the range holds no run at all, production or test. */
export const hasNoRuns = (days: AnalyticsDay[]): boolean => days.every((d) => d.runs === 0 && d.testRuns === 0);

/**
 * Where a chart point drills down to: the runs behind that day, for the
 * environment in the filter, and only the failed ones from the Errors chart.
 */
export function runsForDayLink(params: {
  endpointId: string;
  date: string;
  environment?: string;
  failedOnly?: boolean;
}): string {
  const query = new URLSearchParams({ endpointId: params.endpointId, from: params.date, to: params.date });
  if (params.environment) query.set('environment', params.environment);
  if (params.failedOnly) query.set('status', 'failed');
  return `/collaborations?${query.toString()}`;
}
